import { SET_TODO_ACTIVITIES } from "@data/constants/activitiesConstants"
import { attendanceFlagsService } from "@data/services/attendanceFlagsService"
import { chaptersService } from "@data/services/chaptersService"
import { groupAttributionProposalsService } from "@data/services/groupAttributionProposalsService"
import { groupAttributionService } from "@data/services/groupAttributionService"
import { liveService } from "@data/services/liveService"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import { substitutionProposalsService } from "@data/services/substitutionProposalsService"
import { tutorAvailabilityService } from "@data/services/tutorAvailabilityService"
import {
    activitiesConstants,
    RESET_ACTIVITIES_HISTORY,
} from "../../../constants/activitiesConstants"
import { apiConstants } from "../../../constants/apiConstants"
import { authConstants } from "../../../constants/authConstants"
import { liveModeConstants } from "../../../constants/liveModeConstants"
import { messengerConstants } from "../../../constants/messengerConstants"
import { studentsConstants } from "../../../constants/studentsConstants"
import { achievementsService } from "../../../services/achievementsService"
import { activitiesService } from "../../../services/activitiesService"
import { authService } from "../../../services/authService"
import { challengesService } from "../../../services/challengesService"
import { interviewsService } from "../../../services/interviewsService"
import { messengerService } from "../../../services/messengerService"
import { pedagogicalObjectivesService } from "../../../services/pedagogicalObjectivesService"
import { schoolsService } from "../../../services/schoolsService"
import { schoolSubjectsService } from "../../../services/schoolSubjectsService"
import { shopService } from "../../../services/shopService"
import { skillSetsService } from "../../../services/skillSetsService"
import { studentsService } from "../../../services/studentsService"
import { subscriptionService } from "../../../services/subscriptionService"
import { tutorsService } from "../../../services/tutorsService"
import { findKeyByValue, updateDataKeys } from "../../../utility/Utils"
import { pagedRequestKey } from "../api/pagedRequest"
import { settleLists } from "./liveListFreshness"

const initialState = {
    avatarGenerating: {
        isGenerating: false,
        startTime: null,
    },
}

function withReading(state, reading) {
    if (!state.object?.profile || !reading) return state
    return {
        ...state,
        object: { ...state.object, profile: { ...state.object.profile, reading } },
    }
}

export function currentUser(state = initialState, action) {
    return settleLists(state, reduceCurrentUser(state, action), action)
}

function reduceCurrentUser(state, action) {
    switch (action.type) {
        case liveService.getAllMyNextLiveSessions.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    is_payment_blocked: action.data?.payment_blocked ?? false,
                    payment_url: action.data?.payment_url ?? null,
                },
            }
        case liveService.getAllMyNextLiveSessionsAsParent.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    // Le chemin parent lit le même payload que le chemin élève : sans ces deux
                    // champs, « Paiement en attente » sur l'accueil parent n'avait pas de lien.
                    is_payment_blocked: action.data?.payment_blocked ?? false,
                    payment_url: action.data?.payment_url ?? null,
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            const newSession = action.data?.live_sessions?.find(
                                (newSession) => newSession.id === session.id
                            )
                            return newSession ? newSession : session
                        }
                    ),
                },
            }
        case liveService.reportAbsenceAsStudent.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object.live_sessions.map(
                        (session) => ({
                            ...session,
                            attendance_flags:
                                action.data?.id === session?.id
                                    ? action.data?.attendance_flags
                                    : session?.attendance_flags,
                        })
                    ),
                },
            }
        case attendanceFlagsService.cancelAttendanceFlag.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => ({
                            ...session,
                            attendance_flags: session.attendance_flags?.filter(
                                (flag) => flag.id !== action?.routeParams?.id
                            ),
                        })
                    ),
                },
            }
        case substitutionProposalsService.updateSubstitutionProposalStatus
            .name + apiConstants._SUCCESS:
            const updatedSubProposal = action.data
            return {
                ...state,
                object: {
                    ...state.object,
                    substitution_proposals:
                        state.object?.substitution_proposals?.map((x) =>
                            x.id === action.routeParams?.id
                                ? { ...x, status: updatedSubProposal?.status }
                                : x
                        ),
                },
            }

        case groupAttributionProposalsService
            .updateGroupAttributionProposalStatus.name + apiConstants._SUCCESS:
            const updatedProposal = action.data
            const updateProposalInList = (list) =>
                list?.map((x) =>
                    x.id === action.routeParams?.id
                        ? { ...x, status: updatedProposal?.status }
                        : x
                )
            return {
                ...state,
                object: {
                    ...state.object,
                    group_attribution_proposals: updateProposalInList(
                        state.object?.group_attribution_proposals
                    ),
                    trial_attribution_proposals: updateProposalInList(
                        state.object?.trial_attribution_proposals
                    ),
                },
            }

        case groupAttributionService.getProposalsCount.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    substitution_proposals_meta:
                        action.data.substitution_proposals || {},
                    group_attribution_proposals_meta:
                        action.data.group_attribution_proposals || {},
                    trial_attribution_proposals_meta:
                        action.data.trial_session_proposals || {},
                },
            }
        case groupAttributionProposalsService.getGroupAttributionProposals
            .name + apiConstants._SUCCESS:
            const isFirstPage =
                action.urlParams?.page === 1 || !action.urlParams?.page
            const newGroupProposals = action.data?.data || []
            const isTrial =
                action.urlParams?.is_trial === 1 ||
                action.urlParams?.is_trial === "1"

            // Store in separate arrays based on whether it's trial or group
            if (isTrial) {
                const existingTrialProposals =
                    state.object?.trial_attribution_proposals || []
                return {
                    ...state,
                    object: {
                        ...state.object,
                        trial_attribution_proposals: isFirstPage
                            ? newGroupProposals
                            : [...existingTrialProposals, ...newGroupProposals],
                        trial_attribution_proposals_meta:
                            action.data?.meta || {},
                    },
                }
            } else {
                const existingGroupProposals =
                    state.object?.group_attribution_proposals || []
                return {
                    ...state,
                    object: {
                        ...state.object,
                        group_attribution_proposals: isFirstPage
                            ? newGroupProposals
                            : [...existingGroupProposals, ...newGroupProposals],
                        group_attribution_proposals_meta:
                            action.data?.meta || {},
                    },
                }
            }
        case substitutionProposalsService.getSubstitutionProposals.name +
            apiConstants._SUCCESS:
            const isFirstPageSub =
                action.urlParams?.page === 1 || !action.urlParams?.page
            const existingSubProposals =
                state.object?.substitution_proposals || []
            const newSubProposals = action.data?.data || action.data || []

            // Substitution proposals received successfully
            return {
                ...state,
                object: {
                    ...state.object,
                    substitution_proposals: isFirstPageSub
                        ? newSubProposals
                        : [...existingSubProposals, ...newSubProposals],
                    substitution_proposals_meta: action.data?.meta || {},
                },
            }
        case liveService.moveSession.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            return session.id === action.routeParams?.id
                                ? {
                                      ...action.data,
                                      live_room: session.live_room,
                                      students: session.students,
                                  }
                                : session
                        }
                    ),
                },
            }

        case messengerConstants.IN_LIVE_MESSENGER_NOT_READ_COUNT:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            const id = findKeyByValue(
                                session.thread_ids,
                                action.threadId
                            )
                            const currentCount =
                                session.unread_private_messages_count[
                                    action.threadId === session.thread_id
                                        ? 0
                                        : id
                                ]

                            return action.threadId === session.thread_id
                                ? {
                                      ...session,
                                      unread_messages_count:
                                          action?.data === 0
                                              ? 0
                                              : session.unread_messages_count +
                                                1,
                                  }
                                : id
                                  ? {
                                        ...session,
                                        unread_private_messages_count: {
                                            ...session.unread_private_messages_count,
                                            [id]:
                                                action?.data === 0
                                                    ? 0
                                                    : currentCount + 1,
                                        },
                                    }
                                  : session
                        }
                    ),
                },
            }

        case liveModeConstants.ACTIVITY_FINALIZED:
            let userObject = null
            const session0 = state.object?.live_sessions?.find(
                (s) => s.id === action.sessionId
            )
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            if (session?.id === action?.sessionId) {
                                let users = session.users?.map((u) => {
                                    if (u?.id === action?.studentId) {
                                        return {
                                            ...u,
                                            statistics: {
                                                ...u.statistics,
                                                today: {
                                                    ...u.statistics?.today,
                                                    activities: {
                                                        ...u.statistics?.today
                                                            ?.activities,
                                                        count:
                                                            u.statistics?.today
                                                                ?.activities
                                                                ?.count + 1,
                                                    },
                                                },
                                            },
                                        }
                                    }
                                    return u
                                })
                                return {
                                    ...session,
                                    users: users,
                                }
                            }
                            return session
                        }
                    ),
                },
            }
        case messengerConstants.IN_CHAT_MESSENGER_NOT_READ_COUNT:
            return {
                ...state,
                object: {
                    ...state.object,
                    unreadInChatMessagesCount:
                        action?.data === 0
                            ? 0
                            : state.object?.unreadInChatMessagesCount + 1,
                },
            }
        case messengerConstants.NEW_MESSAGE:
            return {
                ...state,
                object: {
                    ...state.object,
                    new_message: action.data,
                },
            }

        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
        case authService.revokeSanctumToken.name + apiConstants._SUCCESS:
        case authService.deleteAccount.name + apiConstants._SUCCESS:
            return {}

        case authService.bootstrapImpersonation.name + apiConstants._SUCCESS:
            // Hard replace on impersonation: a new tab inherits the previous
            // impersonated identity from redux-persist. Bootstrap's UserResource
            // doesn't load relationships (live_sessions, pedagogical_objectives,
            // profile, …), so merging via updateDataKeys would keep the prior
            // user's nested data — and any name rendered off those fields —
            // visible until the next /api/user lands.
            return {
                object: action.data.user,
            }

        case authService.registerV2.name + apiConstants._SUCCESS:
        case authService.registerTrialV2.name + apiConstants._SUCCESS:
        case authService.registerSpecialV2.name + apiConstants._SUCCESS:
        case authService.registerAndGetSanctumToken.name +
            apiConstants._SUCCESS:
        case authService.getSanctumToken.name + apiConstants._SUCCESS:
        case authService.verifyLoginCode.name + apiConstants._SUCCESS:
            return {
                object: !state.object
                    ? action.data.user
                    : updateDataKeys(action.data.user, state.object),
            }

        case authService.exitImpersonation.name + apiConstants._SUCCESS:
            return {}
        case authConstants.SWITCH_ACCOUNT:
            return {
                object: {
                    id: action.tokenData?.userId,
                    email: action.tokenData?.email,
                    display_name: action.tokenData?.displayName,
                    firstname: action.tokenData?.firstname,
                    lastname: action.tokenData?.lastname,
                    roles: action.tokenData?.roles,
                    avatar: action.tokenData?.avatar,
                    title_skill_set_id: action.tokenData?.title_skill_set_id,
                    hubspot_id: action.tokenData?.hubspot_id,
                    // Preserve impersonation flag across account switches —
                    // the next /api/user call refreshes it from the server
                    // (UserResource re-augments it from the request middleware),
                    // but keeping it here prevents the banner from blinking
                    // off between switch and refetch.
                    impersonation: state.object?.impersonation ?? null,
                },
            }
        case challengesService.abandonChallenge.name + apiConstants._SUCCESS:
        case schoolSubjectsService.removeSchoolSubject.name +
            apiConstants._SUCCESS:
        case schoolSubjectsService.chooseSchoolSubject.name +
            apiConstants._SUCCESS:
        case authService.postNewTrialLiveSessionChoice.name +
            apiConstants._SUCCESS:
        case authService.postPrioritizedSchoolSubjects.name +
            apiConstants._SUCCESS:
        case authService.postStudentEvaluation.name + apiConstants._SUCCESS:
        case authService.postPrepareForTrial.name + apiConstants._SUCCESS:
            return {
                object: !state.object
                    ? action.data
                    : updateDataKeys(action.data, state.object),
            }

        case authService.emailVerify.name + apiConstants._SUCCESS:
            return {
                object: !state.object
                    ? null
                    : {
                          ...state.object,
                          is_verified: true,
                      },
            }

        // The « Ma dispo » writes return a week view, NOT a UserResource — so the
        // generic merge below cannot handle them. Patch the profile keys they change
        // and that other screens read off currentUser (the « Confirme tes créneaux »
        // nudges gate on freshness), else those keep nagging a tutor who just
        // confirmed until the next getUser. `availability_slots` is left alone: the
        // week view appends engaged créneaux the tutor never declared, so it cannot
        // rebuild the declared list faithfully — read the week view for that.
        case tutorAvailabilityService.confirmWeek.name + apiConstants._SUCCESS:
        case tutorAvailabilityService.updateStartingDate.name +
            apiConstants._SUCCESS:
            if (!state.object) return state
            return {
                ...state,
                object: {
                    ...state.object,
                    profile: {
                        ...state.object.profile,
                        availability_freshness: action.data?.freshness,
                        availability_starting_date:
                            action.data?.availability_starting_date,
                    },
                },
            }

        // La fiche « Ma lecture » s'applique dès l'envoi, sans attendre le serveur : l'élève voit
        // son énoncé changer sous ses yeux. La réponse remet ensuite la lecture résolue.
        case authService.updateReadingSettings.name + apiConstants._REQUEST:
            return {
                ...withReading(state, {
                    ...state.object?.profile?.reading,
                    ...action.data,
                    applied_by_default: false,
                }),
                readingBeforeSave: state.object?.profile?.reading,
            }
        // Le serveur a refusé : l'écran revient à la lecture enregistrée, plutôt que d'en montrer
        // une qui disparaîtrait au prochain chargement.
        case authService.updateReadingSettings.name + apiConstants._FAILURE:
            return withReading(state, state.readingBeforeSave)
        case authService.updateReadingSettings.name + apiConstants._SUCCESS:
            return withReading(state, action.data?.reading)

        case shopService.purchase.name + apiConstants._SUCCESS:
        case authService.updateAvatarSelected.name + apiConstants._SUCCESS:
        case authService.updateUserProfile.name + apiConstants._SUCCESS:
        case authService.updateTimezone.name + apiConstants._SUCCESS:
        case authService.updateProfile.name + apiConstants._SUCCESS:
        case authService.updateStudentAccount.name + apiConstants._SUCCESS:
        case authService.updateTutorAccount.name + apiConstants._SUCCESS:
        case authService.onboardingViewed.name + apiConstants._SUCCESS:
        case authService.championIntroViewed.name + apiConstants._SUCCESS:
        case authService.getUser.name + apiConstants._SUCCESS:
        case authService.updateUser.name + apiConstants._SUCCESS:
        case authService.login.name + apiConstants._SUCCESS:
        case authService.register.name + apiConstants._SUCCESS:
        case authService.updateTitleSkillSet.name + apiConstants._SUCCESS:
        case tutorsService.updateSlot.name + apiConstants._SUCCESS:
        case authService.updateSlots.name + apiConstants._SUCCESS:
        case authService.updateStudentSlot.name + apiConstants._SUCCESS:
        case subscriptionService.subscribeToTrial.name + apiConstants._SUCCESS:
        case subscriptionService.subscribeToTrialV2.name +
            apiConstants._SUCCESS:
        case subscriptionService.submitTrialSlots.name + apiConstants._SUCCESS:
        case subscriptionService.pauseSubscriptionParent.name +
            apiConstants._SUCCESS:
        case subscriptionService.cancelParentPause.name + apiConstants._SUCCESS:
        // Cockpit d'été confirm — le back renvoie le UserResource refreshed
        // (avec `summer_summer_status`, `summer_july_status`,
        // `summer_august_status`, `summer_resume_preference`, etc.). Sans ce
        // case, le portail "Mon abonnement" et la bannière restent sur
        // l'ancien snapshot jusqu'au prochain getUser (= reload manuel).
        case subscriptionService.confirmSummerChoice.name +
            apiConstants._SUCCESS:
        // Self-serve activation of the suite — back returns the refreshed
        // UserResource carrying the updated `upcoming_activation` block so the
        // portal flips « suite à activer » → « planifié ensuite » without reload.
        case subscriptionService.activateSubscriptionSuite.name +
            apiConstants._SUCCESS:
        // Prolongation des cours d'été jusqu'à la fin de l'édition — la borne
        // de facturation bouge côté back, donc `subscription_state.end_date` et
        // `upcoming_activation.due_date` changent dans la même réponse.
        case subscriptionService.extendSummerSubscription.name +
            apiConstants._SUCCESS:
        case studentsService.studentChangePaymentCollectionDay.name +
            apiConstants._SUCCESS:
        case schoolsService.joinSchool.name + apiConstants._SUCCESS:
        case tutorsService.submitCompleteProfile.name + apiConstants._SUCCESS:
            // Update each key that was sent by server
            const user = { ...state.object }
            Object.keys(action.data).map((key) => {
                if (key === "live_sessions") {
                    // Hack for getUser that comes after getLiveSession, to keep all keys
                    user[key] = action.data?.[key].map((session) => {
                        const previousSession =
                            user?.[key]?.find((s) => s.id === session.id) ?? {}
                        return {
                            ...previousSession,
                            ...session,
                        }
                    })
                } else {
                    user[key] = action.data[key]
                }
            })
            return {
                ...state,
                object: user,
            }
        case authService.updateBillingAddress.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    address: action.data?.address,
                    parent_firstname:
                        action.data?.parent_firstname ??
                        state.object?.parent_firstname,
                    parent_lastname:
                        action.data?.parent_lastname ??
                        state.object?.parent_lastname,
                },
            }
        case tutorsService.updateNote.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions.map(
                        (session) => {
                            session?.users?.forEach((user) => {
                                user?.tutor_notes?.map((n) =>
                                    n.id === action.data?.id ? action.data : n
                                )
                            })
                            return session
                        }
                    ),
                    tutor_notes: state.object?.tutor_notes?.map((n) =>
                        n.id === action.data?.id ? action.data : n
                    ),
                },
            }
        case tutorsService.deleteNote.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions.map(
                        (session) => {
                            session?.users?.forEach((user) => {
                                user?.tutor_notes?.filter(
                                    (n) => n?.id !== action.routeParams?.id
                                )
                            })
                            return session
                        }
                    ),
                    tutor_notes: state.object?.tutor_notes?.filter(
                        (n) => n?.id !== action.routeParams?.id
                    ),
                },
            }
        case tutorsService.postNote.name + apiConstants._SUCCESS:
            let sessions = []
            sessions = state.object?.live_sessions.map((session) => {
                session?.users?.forEach((user) => {
                    if (user?.id === action?.data?.student_id) {
                        user?.tutor_notes?.push(action?.data)
                    }
                })
                return session
            })
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: sessions,
                    tutor_notes: [action?.data].concat(
                        state.object?.tutor_notes
                    ),
                },
            }
        case tutorsService.getNotes.name + apiConstants._SUCCESS:
            let newNotes = []

            if (action.data?.meta?.current_page === 1) {
                // refresh
                newNotes = action.data?.tutor_notes
            } else {
                // concat
                newNotes = state?.notes.concat(action.data?.tutor_notes)
            }

            return {
                ...state,
                object: {
                    ...state.object,
                    tutor_notes: newNotes,
                },
            }

        case liveService.getLiveSession.name + apiConstants._SUCCESS:
        case liveServiceV2.getLiveSessionV2.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions?.map(
                        (currentSession) => {
                            // hack to keep all keys for activities of users of each live session

                            if (
                                currentSession.id ===
                                action.data?.live_session?.id
                            ) {
                                return {
                                    ...currentSession,
                                    ...action.data?.live_session,

                                    // concat or delete users according to action.data.live_session.users
                                    users: (
                                        currentSession.users
                                            ?.filter(
                                                (u) =>
                                                    !!u &&
                                                    action.data?.live_session?.users
                                                        ?.map((x) => x.id)
                                                        ?.includes(u.id)
                                            )
                                            ?.map((currentUser) => {
                                                const user =
                                                    action.data?.live_session?.users?.find(
                                                        (user) =>
                                                            user.id ===
                                                            currentUser.id
                                                    )
                                                return {
                                                    ...currentUser,
                                                    ...user,
                                                    todo_activities:
                                                        currentUser?.todo_activities
                                                            ?.filter(
                                                                (a) =>
                                                                    !!a &&
                                                                    user?.todo_activities
                                                                        ?.map(
                                                                            (
                                                                                x
                                                                            ) =>
                                                                                x.id
                                                                        )
                                                                        ?.includes(
                                                                            a.id
                                                                        )
                                                            )
                                                            ?.map((a) => {
                                                                const activity =
                                                                    user?.todo_activities?.find(
                                                                        (
                                                                            activity
                                                                        ) =>
                                                                            activity.id ===
                                                                            a.id
                                                                    )
                                                                return {
                                                                    ...a,
                                                                    ...activity,
                                                                }
                                                            })
                                                            ?.concat(
                                                                user?.todo_activities?.filter(
                                                                    (a) =>
                                                                        !currentUser?.todo_activities
                                                                            ?.map(
                                                                                (
                                                                                    x
                                                                                ) =>
                                                                                    x.id
                                                                            )
                                                                            ?.includes(
                                                                                a.id
                                                                            )
                                                                ) ?? []
                                                            ),
                                                    activities:
                                                        currentUser?.activities
                                                            ?.filter(
                                                                (a) =>
                                                                    !!a &&
                                                                    user?.activities
                                                                        ?.map(
                                                                            (
                                                                                x
                                                                            ) =>
                                                                                x.id
                                                                        )
                                                                        ?.includes(
                                                                            a.id
                                                                        )
                                                            )
                                                            ?.map((a) => {
                                                                const activity =
                                                                    user?.activities?.find(
                                                                        (
                                                                            activity
                                                                        ) =>
                                                                            activity.id ===
                                                                            a.id
                                                                    )
                                                                return {
                                                                    ...a,
                                                                    ...activity,
                                                                }
                                                            })
                                                            ?.concat(
                                                                user?.activities?.filter(
                                                                    (a) =>
                                                                        !currentUser?.activities
                                                                            ?.map(
                                                                                (
                                                                                    x
                                                                                ) =>
                                                                                    x.id
                                                                            )
                                                                            ?.includes(
                                                                                a.id
                                                                            )
                                                                ) ?? []
                                                            ),
                                                }
                                            }) ?? []
                                    )?.concat(
                                        action.data?.live_session?.users?.filter(
                                            (u) =>
                                                !(currentSession?.users ?? [])
                                                    ?.map((x) => x.id)
                                                    ?.includes(u.id)
                                        ) ?? []
                                    ),
                                }
                            }

                            return currentSession
                        }
                    ),
                },
            }
        case schoolsService.getClassroom.name + apiConstants._SUCCESS:
        case schoolsService.putClassroom.name + apiConstants._SUCCESS:
        case schoolsService.removeStudentFromClassroom.name +
            apiConstants._SUCCESS:
        case schoolsService.postTeacherChallenge.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    classrooms: (
                        state.object?.classrooms ?? [{ id: action.data?.id }]
                    )?.map((c) =>
                        c?.id === action.data?.id ? action.data : c
                    ),
                },
            }

        case schoolsService.getClassrooms.name + apiConstants._SUCCESS:
        case schoolsService.deleteClassroom.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    classrooms: action.data,
                },
            }

        case schoolsService.postClassroom.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    classrooms: (state.object?.classrooms ?? [])?.concat([
                        action.data,
                    ]),
                },
            }

        case skillSetsService.getSkillSets.name + apiConstants._SUCCESS:
        case skillSetsService.markSkillSetEvolutionAsRead.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    skill_sets: action.data,
                },
            }

        case messengerConstants.INCREMENT_STUDENT_MESSENGER_NOT_READ_COUNT:
            return {
                ...state,
                object: {
                    ...state.object,
                    students: state.object.students?.map((st) => {
                        return action.studentId?.toString() !==
                            st?.id?.toString()
                            ? st
                            : {
                                  ...st,
                                  student_messenger_not_read_count:
                                      (st?.student_messenger_not_read_count ??
                                          0) + 1,
                              }
                    }),
                },
            }

        case messengerConstants.INCREMENT_TUTOR_MESSENGER_NOT_READ_COUNT:
            return {
                ...state,
                object: {
                    ...state.object,
                    tutor_messenger_not_read_count:
                        (state.object?.tutor_messenger_not_read_count ?? 0) + 1,
                },
            }

        case messengerService.getMarkRead.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    // the user is a student : check if it is the thread with his own tutor
                    tutor_messenger_not_read_count:
                        state.object?.tutor_messenger_thread_id ===
                        action.routeParams?.threadId
                            ? 0
                            : state.object?.tutor_messenger_thread_id,
                    // the user is a tutor, update the thread with the student if found
                    students: state.object?.students?.map((st) => {
                        return action.routeParams?.threadId !==
                            st?.tutor_messenger_thread_id
                            ? st
                            : {
                                  ...st,
                                  student_messenger_not_read_count: 0,
                              }
                    }),
                    // user or tutor, update the thread if found in live session
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            if (
                                session.thread_id ===
                                action.routeParams?.threadId
                            ) {
                                return {
                                    ...session,
                                    unread_messages_count: 0,
                                }
                            } else {
                                const userId = findKeyByValue(
                                    session.thread_ids,
                                    action.routeParams?.threadId
                                )

                                return userId
                                    ? {
                                          ...session,
                                          unread_private_messages_count: {
                                              ...session?.unread_private_messages_count,
                                              [userId]: 0,
                                          },
                                      }
                                    : session
                            }
                        }
                    ),
                },
            }
        case challengesService.postChallengeAsTutor.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    students: state.object.students?.map((student) => {
                        return student.id === action.routeParams?.studentId
                            ? {
                                  ...student,
                                  challenges: student?.challenges.concat([
                                      action.data?.new_challenge,
                                  ]),
                                  activities: student?.activities?.concat(
                                      action.data?.new_activities
                                  ),
                              }
                            : student
                    }),
                },
            }
        case liveModeConstants.SET_MESSAGES:
            return {
                ...state,
                object: {
                    messages: state?.object?.messages?.push(action.newState),
                },
            }

        case messengerService.postImage.name + apiConstants._SUCCESS:
        case messengerService.postAudio.name + apiConstants._SUCCESS:
        case messengerService.getPageMessages.name + apiConstants._SUCCESS:
        case messengerService.getMessages.name + apiConstants._SUCCESS:
            let messenger = []
            const threadId = action.routeParams?.threadId
            if (action.all_data?.meta?.page_id === null) {
                // refresh
                messenger = action.data
            } else {
                // concat

                messenger = (state.object?.messages[threadId] ?? [])?.concat(
                    action.data
                )
            }

            return {
                ...state,
                object: {
                    ...state.object,
                    messages: {
                        ...state.object?.messages,
                        [threadId]: messenger,
                    },
                    messageMeta: {
                        ...state.object?.messageMeta,
                        ...action.all_data?.meta,
                    },
                },
            }
        case schoolsService.joinClassroom.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    classrooms: (state.object?.classrooms ?? [])?.concat([
                        action.data,
                    ]),
                },
            }

        case pedagogicalObjectivesService.updateObjectiveLiveSessions.name +
            apiConstants._SUCCESS:
        case pedagogicalObjectivesService.updatePedagogicalObjectiveStatus
            .name + apiConstants._SUCCESS:
        case pedagogicalObjectivesService.updatePedagogicalObjective.name +
            apiConstants._SUCCESS:
        case pedagogicalObjectivesService.updateObjectivesHourAllocation.name +
            apiConstants._SUCCESS:
        case studentsService.getStudent.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    students: state.object.students
                        ?.map((student) => student.id)
                        ?.includes(action.data.id)
                        ? state.object.students?.map((student) => {
                              const u = student
                              const user = action.data

                              return student.id === action.data.id
                                  ? {
                                        ...u,
                                        ...user,
                                        todo_activities:
                                            (u?.todo_activities ?? [])
                                                ?.filter(
                                                    (a) =>
                                                        !!a &&
                                                        user?.todo_activities
                                                            ?.map((x) => x.id)
                                                            ?.includes(a.id)
                                                )
                                                ?.map((a) => {
                                                    const activity =
                                                        user?.todo_activities?.find(
                                                            (activity) =>
                                                                activity.id ===
                                                                a.id
                                                        )
                                                    return {
                                                        ...a,
                                                        ...activity,
                                                    }
                                                })
                                                ?.concat(
                                                    user?.todo_activities?.filter(
                                                        (a) =>
                                                            !u?.todo_activities
                                                                ?.map(
                                                                    (x) => x.id
                                                                )
                                                                ?.includes(a.id)
                                                    ) ?? []
                                                ) ?? [],
                                        activities:
                                            (u?.activities ?? [])
                                                ?.filter(
                                                    (a) =>
                                                        !!a &&
                                                        user?.activities
                                                            ?.map((x) => x.id)
                                                            ?.includes(a.id)
                                                )
                                                ?.map((a) => {
                                                    const activity =
                                                        user?.activities?.find(
                                                            (activity) =>
                                                                activity.id ===
                                                                a.id
                                                        )
                                                    return {
                                                        ...a,
                                                        ...activity,
                                                    }
                                                })
                                                ?.concat(
                                                    user?.activities
                                                        ?.filter(
                                                            (a) =>
                                                                !u?.activities
                                                                    ?.map(
                                                                        (x) =>
                                                                            x.id
                                                                    )
                                                                    ?.includes(
                                                                        a.id
                                                                    )
                                                        )
                                                        ?.map((a) => {
                                                            // Preserve activity_logs from todo_activities for newly finished activities
                                                            const todoActivity =
                                                                u?.todo_activities?.find(
                                                                    (ta) =>
                                                                        ta.id ===
                                                                        a.id
                                                                )
                                                            return todoActivity
                                                                ? {
                                                                      ...todoActivity,
                                                                      ...a,
                                                                  }
                                                                : a
                                                        }) ?? []
                                                ) ?? [],
                                    }
                                  : student
                          })
                        : !state.object.students
                          ? [action.data]
                          : state.object.students?.concat([action.data]),
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            const user = action.data
                            return {
                                ...session,
                                users: session.users?.map((u) =>
                                    u.id === action.data.id
                                        ? {
                                              ...u,
                                              ...user,
                                              todo_activities: (
                                                  u?.todo_activities ?? []
                                              )
                                                  ?.filter(
                                                      (a) =>
                                                          !!a &&
                                                          user?.todo_activities
                                                              .map((x) => x.id)
                                                              ?.includes(a.id)
                                                  )
                                                  ?.map((a) => {
                                                      const activity =
                                                          user?.todo_activities?.find(
                                                              (activity) =>
                                                                  activity.id ===
                                                                  a.id
                                                          )
                                                      return {
                                                          ...a,
                                                          ...activity,
                                                      }
                                                  })
                                                  ?.concat(
                                                      user?.todo_activities?.filter(
                                                          (a) =>
                                                              !u?.todo_activities
                                                                  ?.map(
                                                                      (x) =>
                                                                          x.id
                                                                  )
                                                                  ?.includes(
                                                                      a.id
                                                                  )
                                                      ) ?? []
                                                  ),
                                              activities: u?.activities
                                                  ?.filter(
                                                      (a) =>
                                                          !!a &&
                                                          user?.activities
                                                              .map((x) => x.id)
                                                              ?.includes(a.id)
                                                  )
                                                  ?.map((a) => {
                                                      const activity =
                                                          user?.activities?.find(
                                                              (activity) =>
                                                                  activity.id ===
                                                                  a.id
                                                          )
                                                      return {
                                                          ...a,
                                                          ...activity,
                                                      }
                                                  })
                                                  ?.concat(
                                                      user?.activities
                                                          ?.filter(
                                                              (a) =>
                                                                  !u?.activities
                                                                      ?.map(
                                                                          (x) =>
                                                                              x.id
                                                                      )
                                                                      ?.includes(
                                                                          a.id
                                                                      )
                                                          )
                                                          ?.map((a) => {
                                                              // Preserve activity_logs from todo_activities for newly finished activities
                                                              const todoActivity =
                                                                  u?.todo_activities?.find(
                                                                      (ta) =>
                                                                          ta.id ===
                                                                          a.id
                                                                  )
                                                              return todoActivity
                                                                  ? {
                                                                        ...todoActivity,
                                                                        ...a,
                                                                    }
                                                                  : a
                                                          }) ?? []
                                                  ),
                                          }
                                        : u
                                ),
                            }
                        }
                    ),
                },
            }

        case challengesService.postChallengeAsStudent.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    champion_intros_viewed_at: {
                        ...state.object?.champion_intros_viewed_at,
                        zoe:
                            state.object?.champion_intros_viewed_at?.zoe ??
                            true,
                    },
                    challenges: (state.object?.challenges ?? [])?.concat([
                        action.data?.new_challenge,
                    ]),
                    activities: (state.object?.activities ?? [])?.concat(
                        action.data?.new_activities
                    ),
                },
            }

        case challengesService.getChallengeHistory.name + apiConstants._SUCCESS:
        case challengesService.getStudentChallengeHistory.name +
            apiConstants._SUCCESS:
            const studentId = action.routeParams?.id
            let challengeHistory = []
            let activityHistory = []

            if (action.data?.meta?.current_page === 1) {
                // refresh
                challengeHistory = action.data?.challenges
                activityHistory = action.data?.activities
            } else {
                // concat
                challengeHistory = state.object?.history?.challenges.concat(
                    action.data?.challenges
                )
                activityHistory = state.object?.history?.activities.concat(
                    action.data?.activities
                )
            }

            return {
                ...state,
                object: {
                    ...state.object,
                    history: {
                        ...state.object.history,
                        challenges: challengeHistory,
                        activities: activityHistory,
                    },
                    students: state.object?.students?.map((s) => {
                        return s.id !== studentId
                            ? s
                            : {
                                  ...s,
                                  history: {
                                      ...state.object.history,
                                      challenges: challengeHistory,
                                      activities: activityHistory,
                                  },
                              }
                    }),
                },
            }

        case schoolSubjectsService.getSchoolSubjectHistory.name +
            apiConstants._SUCCESS:
        case schoolSubjectsService.getStudentSchoolSubjectHistory.name +
            apiConstants._SUCCESS:
            const stId = action.routeParams?.id
            let ssHistory = []
            let aHistory = []

            if (action.data?.meta?.current_page === 1) {
                // refresh
                ssHistory = action.data?.school_subjects
                aHistory = action.data?.activities
            } else {
                // concat
                ssHistory = state.object?.history?.school_subjects.concat(
                    action.data?.school_subjects
                )
                aHistory = state.object?.history?.activities.concat(
                    action.data?.activities
                )
            }

            return {
                ...state,
                object: {
                    ...state.object,
                    history: {
                        ...state.object.history,
                        school_subjects: ssHistory,
                        activities: aHistory,
                    },
                    students: state.object?.students?.map((s) => {
                        return s.id !== stId
                            ? s
                            : {
                                  ...s,
                                  history: {
                                      ...state.object.history,
                                      school_subjects: ssHistory,
                                      activities: aHistory,
                                  },
                              }
                    }),
                },
            }

        case activitiesService.getFinishedActivities.name +
            apiConstants._REQUEST:
        case activitiesService.getStudentFinishedActivities.name +
            apiConstants._REQUEST:
            return {
                ...state,
                finishedActivitiesQuery: pagedRequestKey(action),
            }

        case activitiesService.getFinishedActivities.name +
            apiConstants._SUCCESS:
        case activitiesService.getStudentFinishedActivities.name +
            apiConstants._SUCCESS:
            // La réponse d'une recherche dépassée, arrivée après la dernière, ne remplace pas la liste.
            if (
                state.finishedActivitiesQuery != null &&
                pagedRequestKey(action) !== state.finishedActivitiesQuery
            ) {
                return state
            }
            const stuId = action.routeParams?.id
            let finishedActivities = []
            let challenges = []

            if (action.data?.meta?.current_page === 1) {
                // refresh
                finishedActivities = action.data?.activities
                challenges = action.data?.challenges
            } else {
                // concat
                finishedActivities = state.object?.history?.activities.concat(
                    action.data?.activities
                )
                challenges = state.object?.history?.challenges.concat(
                    action.data?.challenges
                )
            }

            // Le total et les matières de la recherche des activités faites voyagent avec la liste.
            const finishedHistory = {
                ...state.object.history,
                activities: finishedActivities,
                challenges: challenges,
                activities_meta: action.data?.meta,
            }

            return {
                ...state,
                object: {
                    ...state.object,
                    history: finishedHistory,
                    students: state.object?.students?.map((s) => {
                        return s.id !== stuId
                            ? s
                            : {
                                  ...s,
                                  history: finishedHistory,
                              }
                    }),
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            return {
                                ...session,
                                users: session.users?.map((u) => {
                                    return u.id !== stuId
                                        ? u
                                        : {
                                              ...u,
                                              history: finishedHistory,
                                          }
                                }),
                            }
                        }
                    ),
                },
            }

        case RESET_ACTIVITIES_HISTORY:
            const sId = action?.studentId ?? null

            return {
                ...state,
                object: {
                    ...state.object,
                    history: {
                        ...state.object.history,
                        activities: [],
                    },
                    students: state.object?.students?.map((s) => {
                        return s.id !== sId
                            ? s
                            : {
                                  ...s,
                                  history: {
                                      ...state.object.history,
                                      activities: [],
                                  },
                              }
                    }),
                },
            }

        case activitiesService.getActivitiesForReview.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    pending_reviews_count: action.data?.pending_reviews_count,
                },
            }

        case activitiesService.reviewActivity.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    pending_reviews_count:
                        state.object?.pending_reviews_count > 0
                            ? state.object?.pending_reviews_count - 1
                            : 0,
                },
            }

        //** TO DO
        case SET_TODO_ACTIVITIES:
            const todoStudentId = action?.todoActivities?.[0]?.student_id

            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: action.todoActivities,
                    students: state.object?.students?.map((s) =>
                        s?.id !== todoStudentId
                            ? s
                            : {
                                  ...s,
                                  todo_activities: action.todoActivities,
                              }
                    ),
                    live_sessions: state.object?.live_sessions?.map((ls) =>
                        !ls?.users?.map((u) => u?.id)?.includes(todoStudentId)
                            ? ls
                            : {
                                  ...ls,
                                  users: ls?.users?.map((u) =>
                                      u?.id !== todoStudentId
                                          ? u
                                          : {
                                                ...u,
                                                todo_activities:
                                                    action.todoActivities,
                                            }
                                  ),
                              }
                    ),
                },
            }

        case activitiesService.updateActivityTodo.name + apiConstants._SUCCESS:
        case activitiesService.getActivity.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: state.object.todo_activities?.map(
                        (todo) =>
                            todo?.id === action?.data?.id ? action?.data : todo
                    ),
                    students: state.object?.students?.map((s) =>
                        s.id !== action?.data?.student_id
                            ? s
                            : {
                                  ...s,
                                  todo_activities: s?.todo_activities?.map(
                                      (todo) =>
                                          todo?.id === action?.data?.id
                                              ? action?.data
                                              : todo
                                  ),
                              }
                    ),
                    live_sessions: state.object?.live_sessions?.map((ls) => {
                        return !ls?.users
                            ?.map((u) => u?.id)
                            ?.includes(action?.data?.student_id)
                            ? ls
                            : {
                                  ...ls,
                                  users: ls?.users?.map((u) =>
                                      u?.id !== action?.data?.student_id
                                          ? u
                                          : {
                                                ...u,
                                                todo_activities:
                                                    u?.todo_activities?.map(
                                                        (todo) =>
                                                            todo?.id ===
                                                            action?.data?.id
                                                                ? action?.data
                                                                : todo
                                                    ),
                                            }
                                  ),
                              }
                    }),
                },
            }

        case chaptersService.studentChapterFeelings.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    ongoing_student_chapters:
                        state.object?.ongoing_student_chapters?.map((x) =>
                            x?.id === action?.data?.id ? action?.data : x
                        ),
                },
            }

        case activitiesService.activityTodoAsTutor.name + apiConstants._SUCCESS:
        case activitiesService.createActivityAsTutor.name +
            apiConstants._SUCCESS:
        case messengerService.postActivityStudent.name + apiConstants._SUCCESS:
        case messengerService.postActivity.name + apiConstants._SUCCESS:
            const data = action?.data

            return {
                ...state,
                object: {
                    ...state.object,
                    students: state.object.students?.map((s) =>
                        s.id !== data?.student_id
                            ? s
                            : {
                                  ...s,
                                  todo_activities:
                                      s?.todo_activities?.concat(data),
                                  ongoing_student_chapters:
                                      s?.ongoing_student_chapters
                                          ?.map((x) => x?.id)
                                          ?.includes(data?.student_chapter?.id)
                                          ? s?.ongoing_student_chapters
                                          : s?.ongoing_student_chapters?.concat(
                                                data?.student_chapter
                                            ),
                              }
                    ),
                    live_sessions: state.object?.live_sessions?.map((ls) => {
                        return !ls?.users
                            ?.map((u) => u?.id)
                            ?.includes(data?.student_id)
                            ? ls
                            : {
                                  ...ls,
                                  users: ls?.users?.map((u) =>
                                      u?.id !== data?.student_id
                                          ? u
                                          : {
                                                ...u,
                                                todo_activities:
                                                    u?.todo_activities?.concat(
                                                        data
                                                    ),
                                                ongoing_student_chapters:
                                                    u?.ongoing_student_chapters
                                                        ?.map((x) => x?.id)
                                                        ?.includes(
                                                            data
                                                                ?.student_chapter
                                                                ?.id
                                                        )
                                                        ? u?.ongoing_student_chapters
                                                        : u?.ongoing_student_chapters?.concat(
                                                              data?.student_chapter
                                                          ),
                                            }
                                  ),
                              }
                    }),
                },
            }
        case activitiesService.deleteCommonActivityTodo.name +
            apiConstants._SUCCESS:
        case activitiesService.activityTodoForStudents.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    live_sessions: state.object?.live_sessions?.map(
                        (session) => {
                            if (session.id === action.data?.live_session?.id) {
                                return action.data.live_session
                            }

                            return session
                        }
                    ),
                },
            }
        case activitiesService.activityTodoAsStudent.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: state.object?.todo_activities?.concat(
                        action?.data
                    ),
                    ongoing_student_chapters:
                        state.object?.ongoing_student_chapters
                            ?.map((x) => x?.id)
                            ?.includes(action?.data?.student_chapter?.id)
                            ? state.object?.ongoing_student_chapters
                            : state.object?.ongoing_student_chapters?.concat(
                                  action?.data?.student_chapter
                              ),
                },
            }
        case activitiesService.deleteActivitiesTodo.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: state.object?.todo_activities?.filter(
                        (todo) =>
                            !action.data?.deleted_activity_ids?.includes(
                                todo?.id
                            )
                    ),
                    students: state.object?.students?.map((s) => {
                        return {
                            ...s,
                            todo_activities: s.todo_activities?.filter(
                                (todo) =>
                                    !action.data?.deleted_activity_ids?.includes(
                                        todo?.id
                                    )
                            ),
                        }
                    }),
                    live_sessions: state.object?.live_sessions?.map((ls) => {
                        return {
                            ...ls,
                            users: ls?.users?.map((u) => {
                                return {
                                    ...u,
                                    todo_activities: u?.todo_activities?.filter(
                                        (todo) =>
                                            !action.data?.deleted_activity_ids?.includes(
                                                todo?.id
                                            )
                                    ),
                                }
                            }),
                        }
                    }),
                },
            }
        case activitiesService.restoreArchivedActivities.name +
            apiConstants._SUCCESS:
            const restoredActivities = action.data?.activities || []
            const restoredStudentId = restoredActivities?.[0]?.student_id

            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities:
                        restoredStudentId === state.object?.id
                            ? [
                                  ...restoredActivities,
                                  ...(state.object?.todo_activities || []),
                              ]
                            : state.object?.todo_activities,
                    students: state.object?.students?.map((s) => {
                        return s.id !== restoredStudentId
                            ? s
                            : {
                                  ...s,
                                  todo_activities: [
                                      ...restoredActivities,
                                      ...(s.todo_activities || []),
                                  ],
                              }
                    }),
                    live_sessions: state.object?.live_sessions?.map((ls) => {
                        return {
                            ...ls,
                            users: ls?.users?.map((u) => {
                                return u.id !== restoredStudentId
                                    ? u
                                    : {
                                          ...u,
                                          todo_activities: [
                                              ...restoredActivities,
                                              ...(u.todo_activities || []),
                                          ],
                                      }
                            }),
                        }
                    }),
                },
            }
        case activitiesService.deleteActivityLog.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: state.object?.todo_activities?.map(
                        (activity) => {
                            return {
                                ...activity,
                                activity_logs: activity?.activity_logs?.filter(
                                    (log) => log?.id !== action.routeParams?.id
                                ),
                            }
                        }
                    ),
                },
            }
        case activitiesService.lockActivityV2.name + apiConstants._SUCCESS:
        case activitiesService.postActivityLogV1.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: state.object?.todo_activities?.map(
                        (activity) => {
                            return activity.id === action.data?.id
                                ? action.data
                                : activity
                        }
                    ),
                },
            }
        case activitiesService.activityTodoIndexesAsStudent.name +
            apiConstants._SUCCESS:
        case activitiesService.activityTodoIndexesAsTutor.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: action?.data,
                },
            }
        case activitiesService.getActivities.name + apiConstants._SUCCESS:
            const updateActivityIfFoundInResponse = (a) => {
                return action.data?.find((x) => x?.id === a?.id) ?? a
            }

            return {
                ...state,
                object: {
                    ...state.object,
                    activities: state.object?.activities?.map(
                        updateActivityIfFoundInResponse
                    ),
                    history: {
                        ...state.object.history,
                        activities: state.object?.history?.activities?.map(
                            updateActivityIfFoundInResponse
                        ),
                    },
                    students: state.object?.students?.map((s) => {
                        return !action.data?.find(
                            (x) => x?.student_id === s?.id
                        )
                            ? s
                            : {
                                  ...s,
                                  activities: s?.activities?.map(
                                      updateActivityIfFoundInResponse
                                  ),
                                  todo_activities: s?.todo_activities?.map(
                                      updateActivityIfFoundInResponse
                                  ),
                                  history: {
                                      ...s?.history,
                                      activities: s?.history?.activities?.map(
                                          updateActivityIfFoundInResponse
                                      ),
                                  },
                              }
                    }),
                    live_sessions: state.object?.live_sessions?.map((ls) => {
                        return !ls?.users
                            ?.map((u) => u?.id)
                            ?.includes(action?.data?.[0]?.student_id)
                            ? ls
                            : {
                                  ...ls,
                                  users: ls?.users?.map((u) =>
                                      u?.id !== action?.data?.[0]?.student_id
                                          ? u
                                          : {
                                                ...u,
                                                activities: u?.activities?.map(
                                                    updateActivityIfFoundInResponse
                                                ),
                                                todo_activities:
                                                    u?.todo_activities?.map(
                                                        updateActivityIfFoundInResponse
                                                    ),
                                                history: {
                                                    ...u?.history,
                                                    activities:
                                                        u?.history?.activities?.map(
                                                            updateActivityIfFoundInResponse
                                                        ),
                                                },
                                            }
                                  ),
                              }
                    }),
                },
            }

        case activitiesService.initActivity.name + apiConstants._SUCCESS:
            // if there is no user in the response, it is a true init success, else, it is like finalize
            if (!action.data?.user?.id) {
                const updateSingleActivity = (activity) => {
                    return activity.id === action.data?.id
                        ? action.data
                        : activity
                }
                let newActivities = []
                const stateActivity = state.object?.activities?.find(
                    (x) => x.id === action.data?.id
                )
                if (stateActivity) {
                    newActivities =
                        state.object?.activities?.map(updateSingleActivity)
                } else {
                    newActivities = [...state.object?.activities]
                    newActivities.push(action.data)
                }

                return {
                    ...state,
                    object: {
                        ...state.object,
                        activities: newActivities,
                    },
                }
            }

        case activitiesService.readReview.name + apiConstants._SUCCESS:
        case activitiesService.finalizeActivity.name + apiConstants._SUCCESS:
        case activitiesService.finalizeActivitySilent.name +
            apiConstants._SUCCESS:
            // 204 No Content (e.g. readReview when already read) → nothing to merge
            if (!action.data?.user) return state
            const u = { ...state.object }
            Object.keys(action.data?.user ?? {}).map((key) => {
                if (key !== "live_sessions") {
                    // prevent bug during live session where live session data is erased
                    u[key] = action.data?.user[key]
                }
            })

            const updateActivity = (activity) => {
                return activity.id === action.data?.activity?.id
                    ? action.data?.activity
                    : activity
            }

            const updateChallenge = (challenge) => {
                return challenge.id === action.data?.challenge?.id
                    ? action.data?.challenge
                    : challenge
            }

            return {
                object: {
                    ...u,
                    history: {
                        ...u?.history,
                        activities: [],
                        challenges:
                            u?.history?.challenges?.map(updateChallenge),
                    },
                    activities:
                        action.data.user?.activities?.map(updateActivity),
                    todo_activities: action.data.user?.todo_activities?.filter(
                        (activity) => activity.id !== action.data?.activity?.id
                    ),
                    skill_sets: action.data?.skill_sets,
                },
            }

        case achievementsService.markAsRead.name + apiConstants._SUCCESS:
            const updateAchievement = (achievement) => {
                return achievement.id === action.data?.id
                    ? action.data
                    : achievement
            }
            return {
                ...state,
                object: {
                    ...state.object,
                    achievements:
                        state.object?.achievements?.map(updateAchievement),
                },
            }

        case tutorsService.chooseTutor.name + apiConstants._SUCCESS:
            return {
                object: action.data,
            }

        case studentsService.getStudents.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    students: action?.data?.map((s) => {
                        return {
                            ...s,
                            todo_activities: state.object?.students?.find(
                                (student) => student?.id === s.id
                            )?.todo_activities,
                            challenges: state.object?.students?.find(
                                (student) => student?.id === s.id
                            )?.challenges,
                        }
                    }),
                },
            }

        case challengesService.abandonChallengeAsTutor.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    students: state.object.students?.map((student) => {
                        return student.id === action.routeParams?.studentId
                            ? action.data
                            : student
                    }),
                },
            }

        case interviewsService.interviewWeekComments.name +
            apiConstants._SUCCESS:
        case interviewsService.interviewGlobalComment.name +
            apiConstants._SUCCESS:
        case interviewsService.interviewFinalize.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    students: state.object?.students?.map((student) => {
                        return student?.id === action.data.student_id
                            ? {
                                  ...student,
                                  interviews: student?.interviews.map(
                                      (interview) => {
                                          return interview?.id ===
                                              action.data.id
                                              ? action.data
                                              : interview
                                      }
                                  ),
                              }
                            : student
                    }),
                },
            }
        case activitiesService.postMaterial.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    activities: state.object?.activities?.map((a) => {
                        return a?.id === action?.routeParams?.activity_id
                            ? { ...a, material: action?.data }
                            : a
                    }),
                    todo_activities: state.object?.todo_activities?.map((a) => {
                        return a?.id === action?.routeParams?.activity_id
                            ? { ...a, material: action?.data }
                            : a
                    }),
                },
            }

        case activitiesService.putMaterial.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    activities: state.object?.activities?.map((a) => {
                        return a?.material?.id === action?.data?.id
                            ? { ...a, material: action?.data }
                            : a
                    }),
                    todo_activities: state.object?.todo_activities?.map((a) => {
                        return a?.material?.id === action?.data?.id
                            ? { ...a, material: action?.data }
                            : a
                    }),
                },
            }

        case activitiesService.deleteMaterial.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    activities: state.object?.activities?.map((a) => {
                        return a?.material?.id === action?.routeParams?.id
                            ? { ...a, material: null }
                            : a
                    }),
                    todo_activities: state.object?.todo_activities?.map((a) => {
                        return a?.material?.id === action?.routeParams?.id
                            ? { ...a, material: null }
                            : a
                    }),
                },
            }

        case authService.hasMobileSession.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    has_mobile_session: action.data?.has_mobile_session,
                },
            }

        case activitiesConstants.MARK_ACTIVITIES_AS_DELETED:
            return {
                ...state,
                object: {
                    ...state.object,
                    todo_activities: state.object?.todo_activities?.map(
                        (activity) => {
                            if (action.activityIds.includes(activity.id)) {
                                return { ...activity, isDeleted: true }
                            }
                            return activity
                        }
                    ),
                },
            }

        case tutorsService.getTutorScore.name + apiConstants._SUCCESS:
            return {
                ...state,
                object: {
                    ...state.object,
                    tutor_score: action?.data,
                },
            }

        case studentsConstants.START_AVATAR_GENERATION:
            return {
                ...state,
                avatarGenerating: {
                    isGenerating: true,
                    startTime: Date.now(),
                },
            }

        case studentsConstants.STOP_AVATAR_GENERATION:
            return {
                ...state,
                avatarGenerating: {
                    isGenerating: false,
                    startTime: null,
                },
            }

        default:
            return state
    }
}
